import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { ExternalOrderItem } from '@1po/1po-bff-fe-spec/generated/order/response/model/ExternalOrderItem';
import { PresentableOrderStatusType } from '@1po/1po-bff-fe-spec/generated/order/response/model/PresentableOrderStatusType';
import { ROUTER_ORDERS } from 'app/AppRouter';
import { CopyToClipboardButton } from 'components/CopyToClipboardButton/CopyToClipboardButton';
import { toDate } from 'domains/order/OrderDate';
import { OrderStatus } from 'domains/order/OrderStatus';
import { SOrdersTable } from 'pages/MyOrdersPage/ExternalOrders/OrdersTable/OrdersTable.styled';
import { EXTERNAL } from 'pages/MyOrdersPage/Orders.type';
import { Flex, MarginBox, Text } from 'UI';
import { textFormatter } from 'utils/format';

const VehicleCell = ({ vehicleDetail }: { vehicleDetail: VehicleDetail | undefined }) => {
  const { t } = useTranslation();

  if (!vehicleDetail) {
    return null;
  }

  const vehicleBrand = vehicleDetail.vehicleBrand?.replaceAll('_', ' ');
  const vehicleModel = vehicleDetail.model ?? vehicleDetail.dataHubVehicle?.name;
  const vehicleLabel = [vehicleBrand, vehicleModel].filter(Boolean).join(' ');

  return (
    <Flex direction={'row'} align={'center'} data-testid={'vehicle-context'}>
      <Flex direction={'column'}>
        {vehicleLabel && (
          <Text type="base">
            <strong>{vehicleLabel}</strong>
          </Text>
        )}
        {vehicleDetail.vin && (
          <Flex direction={'row'} align={'center'}>
            <Text type="small">{`${t('catalog.vehicle.vin', 'VIN')}: ${vehicleDetail.vin}`}</Text>
            <MarginBox ml={4} />
            <CopyToClipboardButton
              textToCopy={vehicleDetail.vin}
              tooltip={t('common.action.copy_to_clipboard', 'Copy to clipboard')}
              size={12}
            />
          </Flex>
        )}
        {vehicleDetail.vrn && (
          <Flex direction={'row'} align={'center'}>
            <Text type="small">{`${t('catalog.vehicle.vrn', 'VRN')}: ${vehicleDetail.vrn}`}</Text>
            <MarginBox ml={4} />
            <CopyToClipboardButton
              textToCopy={vehicleDetail.vrn}
              tooltip={t('common.action.copy_to_clipboard', 'Copy to clipboard')}
              size={12}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const orderColumns = () => [
  {
    title: (
      <Text type="heading3">
        <Trans i18nKey={'order.order_detail.order_number'}>Order number</Trans>
      </Text>
    ),
    dataIndex: 'orderId',
    key: 'orderId',
    render: function rowSelector(orderId: string) {
      return (
        <Flex>
          <Flex direction={'column'} align={'center'}>
            <Text type="lg">
              <strong>{orderId}</strong>
            </Text>
          </Flex>
        </Flex>
      );
    },
  },
  {
    title: (
      <Text type="heading3">
        <Trans i18nKey={'order.order_detail.vehicle'}>Vehicle</Trans>
      </Text>
    ),
    dataIndex: 'vehicleDetail',
    key: 'vehicleDetail',
    render: function rowSelector(_: unknown, orderItem: ExternalOrderItem) {
      return <VehicleCell vehicleDetail={orderItem.vehicleDetail} />;
    },
  },
  {
    title: (
      <Text type="heading3">
        <Trans i18nKey={'order.order_detail.order_date.title'}>Order date</Trans>
      </Text>
    ),
    dataIndex: 'creationDate',
    key: 'creationDate',
    render: function rowSelector(creationDate: string) {
      return (
        <Flex direction={'column'} align={'center'}>
          {creationDate && <Text type="base">{toDate(creationDate)}</Text>}
        </Flex>
      );
    },
  },
  {
    title: (
      <Text type="heading3">
        <Trans i18nKey={'order.order_detail.price'}>Price</Trans>
      </Text>
    ),
    dataIndex: 'totalPriceTaxExcluded',
    key: 'price',
    render: function rowSelector(priceVatExcluded: number, orderItem: ExternalOrderItem) {
      return (
        <Flex direction={'column'} align={'center'}>
          <Text type="base">{textFormatter.formatCurrency(priceVatExcluded, orderItem.currency)}</Text>
        </Flex>
      );
    },
  },
  {
    title: (
      <Text type="heading3">
        <Trans i18nKey={'order.order_detail.status'}>Status</Trans>
      </Text>
    ),
    dataIndex: 'presentableOrderStatus',
    key: 'presentableOrderStatus',
    render: function rowSelector(orderStatus: PresentableOrderStatusType) {
      return (
        <Flex direction={'column'} align={'center'}>
          <OrderStatus orderStatus={orderStatus} />
        </Flex>
      );
    },
  },
  {
    title: (
      <Text type="heading3">
        <Trans i18nKey={'order.order_detail.ordered_by'}>Ordered by</Trans>
      </Text>
    ),
    dataIndex: 'application',
    key: 'application',
    render: function rowSelector(application: string) {
      return (
        <Flex direction={'column'} align={'center'}>
          <Text type="base">{`${application}`}</Text>
        </Flex>
      );
    },
  },
];

const OrdersTable = ({ orders }: { orders: ExternalOrderItem[] }) => {
  const history = useHistory();
  const columns = orderColumns();

  return (
    <SOrdersTable<ExternalOrderItem>
      columns={columns}
      dataSource={orders}
      pagination={false}
      rowKey={(order) => order.orderId}
      size={'small'}
      bordered={true}
      onRow={(order) => {
        return {
          onClick: () => {
            history.push(`${ROUTER_ORDERS}/${EXTERNAL}/${order.orderId}`);
          },
        };
      }}
    />
  );
};

export default OrdersTable;
